import { useEffect, useState } from "react"
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Button,
  Paper,
  TextField,
  Divider,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Alert
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import SaveIcon from "@mui/icons-material/Save"
import VisibilityIcon from "@mui/icons-material/Visibility"
import SendIcon from "@mui/icons-material/Send"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import DownloadFileIcon from "@mui/icons-material/Download"
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useDispatch, useSelector } from 'react-redux';
import { fetchAccountsFromAPI } from '../../Redux/AccountDucks';
import { downloadCampaignFormat, fetchCampaignsFromAPI ,sendCampaignAPI } from '../../Redux/CampaignDucks';
import { listSimpleTemplatesAPI } from '../../Redux/TemplateDucks';

// Datos de ejemplo de campañas
const initialCampaigns = [
  {
    id: 1,
    name: `Nueva Campaña`,
    timestamp: "Ahora",
    time: new Date().toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    }),
    description: "Descripción de la nueva campaña...",
    template: "0",
  },
]

const getStatusIcon = (status) => {
  switch (status) {
    case 'SENT':
      return {
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        color: 'success.main', // Verde para éxito/enviado
      };
    case 'CREATED':
      return {
        icon: <AccessTimeIcon fontSize="small" />,
        color: 'text.secondary', // Gris para pendiente/creado
      };
    case 'ERROR':
      return {
        icon: <WarningAmberIcon fontSize="small" />,
        color: 'warning.main', // Naranja/Amarillo para advertencia/error
      };
    default:
      return { icon: null, color: 'text.secondary' }; // Por defecto, si el estado no coincide
  }
};

export default function CampaignListPage() {
  const [campaigns, setCampaigns] = useState(initialCampaigns)
  const [selectedCampaign, setSelectedCampaign] = useState(campaigns[0])
  const [selectedAccount, setSelectedAccount] = useState('0')
  const [selectedFile, setSelectedFile] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [templates, setTemplates] = useState([])
  const [loadingExcel, setLoadingExcel] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const dispatch = useDispatch()
  const accountList = useSelector(store => store.account.accounts)
  const templateList = useSelector(store => store.template.simpleTemplates)
  const campaignList = useSelector(store => store.campaign.campaigns)

  useEffect(() => {
    dispatch(fetchAccountsFromAPI());
  }, [])

  useEffect(() => {
    setTemplates(templateList);
  }, [templateList])

  useEffect(() => {
    if (accountList.length > 0) {
      setAccounts(accountList)
    }
  }, [accountList])

  useEffect(() => {
    setCampaigns(campaignList);
  }, [campaignList])

  const handleSelectCampaign = (campaign) => {
    setSelectedCampaign(campaign)
  }

  const handleSelectAccount = (accountId) => {
    setSelectedAccount(accountId)
    dispatch(listSimpleTemplatesAPI(String(accountId)));
    dispatch(fetchCampaignsFromAPI(String(accountId)));
  }

  const handleCreateCampaign = () => {
    const newCampaign = {
      id: campaigns.length + 1,
      name: `Nueva Campaña`,
      timestamp: "Ahora",
      time: new Date().toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      description: "Descripción de la nueva campaña...",
      template: "gpt-5.4",
    }
    setCampaigns([...campaigns, newCampaign])
    setSelectedCampaign(newCampaign)
  }

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0])
    }
  }

  const handleSendCampaign = async () => {
    try {
      // Limpiar alertas previas
      setError(null)
      setSuccess(null)
      setSending(true)

      // Validaciones
      if (!selectedFile) {
        setError('Por favor selecciona un archivo')
        setSending(false)
        return
      }

      if (!selectedCampaign.template || selectedCampaign.template === '0') {
        setError('Por favor selecciona una plantilla')
        setSending(false)
        return
      }

      if (!selectedAccount || selectedAccount === '0') {
        setError('Por favor selecciona una cuenta')
        setSending(false)
        return
      }

      console.log("Enviando campaña con archivo:", selectedFile)
      await dispatch(sendCampaignAPI(selectedCampaign, selectedFile))
      
      // Si todo sale bien
      setSuccess(true)
      setSelectedFile(null) // Limpiar archivo seleccionado
      
    } catch (err) {
      console.error('Error al enviar campaña:', err)
      setError(err.message || 'Error al enviar la campaña')
    } finally {
      setSending(false)
    }
  }

  return (
    <Box sx={{ display: "flex", height: "88vh", bgcolor: "#fafafa" }}>
      {/* Panel izquierdo - Lista de campañas */}
      <Paper
        elevation={0}
        sx={{
          width: 320,
          borderRight: "1px solid #e0e0e0",
          display: "flex",
          flexDirection: "column",
          bgcolor: "white",
        }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#666", fontSize: "0.75rem", fontWeight: 600 }}>
            Cuentas
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={selectedAccount}
              onChange={(e) => handleSelectAccount(e.target.value)}
              sx={{
                bgcolor: "white",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1a1a1a",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1a1a1a",
                },
              }}
            >
              <MenuItem key='0' value='0'>
                  Seleccione una cuenta
              </MenuItem>
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
            Campañas
          </Typography>
        </Box>

        <List sx={{ flex: 1, overflow: "auto", p: 0 }}>
          {campaigns.map((campaign, index) => {
            const statusInfo = getStatusIcon(campaign.status)

            return (
            <Box key={campaign.id}>
              {index > 0 && campaigns[index - 1].timestamp !== campaign.timestamp && (
                <Box sx={{ px: 2, py: 1, bgcolor: "#f5f5f5" }}>
                  <Typography variant="caption" sx={{ color: "#666", fontSize: "0.75rem" }}>
                    {campaign.timestamp}
                  </Typography>
                </Box>
              )}
              <ListItem disablePadding>
                <ListItemButton
                  selected={selectedCampaign?.id === campaign.id}
                  onClick={() => handleSelectCampaign(campaign)}
                  sx={{
                    py: 2,
                    px: 2,
                    "&.Mui-selected": {
                      bgcolor: "#f0f0f0",
                      "&:hover": {
                        bgcolor: "#e8e8e8",
                      },
                    },
                    "&:hover": {
                      bgcolor: "#f5f5f5",
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Contenedor para el Nombre + Icono */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: "#1a1a1a",
                    fontSize: "0.9rem",
                  }}
                >
                  {campaign.name}
                </Typography>
                {/* Ícono de Estado Agregado Aquí */}
                {statusInfo.icon && (
                  <Box
                    component="span"
                    sx={{
                      color: statusInfo.color, // Color dinámico
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {statusInfo.icon}
                  </Box>
                )}
              </Box>

              {/* Hora */}
              <Typography variant="caption" sx={{ color: "#666", fontSize: "0.75rem" }}>
                {campaign.time}
              </Typography>
            </Box>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#999",
                          fontSize: "0.7rem",
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        {campaign.code}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            </Box>
)})}
        </List>

        <Box sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateCampaign}
            sx={{
              textTransform: "none",
              borderColor: "#e0e0e0",
              color: "#1a1a1a",
              "&:hover": {
                borderColor: "#1a1a1a",
                bgcolor: "#f5f5f5",
              },
            }}
          >
            Crear nueva campaña
          </Button>
        </Box>
      </Paper>

      {/* Panel derecho - Detalle de campaña */}
      <Box sx={{ flex: 1, overflow: "auto", bgcolor: "white" }}>
        {selectedCampaign && (
          <Box sx={{ maxWidth: 800, mx: "auto", p: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="overline" sx={{ color: "#666", fontWeight: 600, letterSpacing: 1 }}>
                CAMPAÑA
              </Typography>
              
            </Box>

            {/* Alertas */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                Campaña enviada exitosamente
              </Alert>
            )}

            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "#1a1a1a", fontWeight: 600 }}>
                Nombre
              </Typography>
              <TextField
                fullWidth
                value={selectedCampaign.name}
                onChange={(e) => setSelectedCampaign({ ...selectedCampaign, name: e.target.value })}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: "#1a1a1a", fontWeight: 600 }}>
                  Descripción
                </Typography>
                {/* <IconButton size="small">
                  <EditIcon fontSize="small" />
                </IconButton> */}
              </Box>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={selectedCampaign.description}
                onChange={(e) => setSelectedCampaign({ ...selectedCampaign, description: e.target.value })}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "#1a1a1a", fontWeight: 600 }}>
                Plantilla Whatsapp
              </Typography>
              <Select
                fullWidth
                onChange ={(e) => setSelectedCampaign({ ...selectedCampaign, template: e.target.value })}
                value={selectedCampaign.template}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                  },
                }}
              >
                <MenuItem key='0' value='0'>
                  Seleccione una plantilla
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, color: "#1a1a1a", fontWeight: 600 }}>
                Enviar campaña
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                  
                  <Button 
                    variant="outlined"
                    disabled={loadingExcel}
                    component="label"
                    onClick={async () => {
                      if (selectedCampaign.template != '0' && selectedCampaign.template != 0) {
                        setLoadingExcel(true);
                        var templateId = selectedCampaign.template;
                        var template  = templates.find(f => f.id == templateId);
                        await new Promise(resolve => setTimeout(resolve, 300));
                        await dispatch(downloadCampaignFormat(String(templateId),template.name));
                        setLoadingExcel(false);
                      }
                      
                    }}
                    startIcon={loadingExcel ? <CircularProgress size={16} color="inherit" /> : <DownloadFileIcon />}
                    sx={{
                      textTransform: "none"
                    }}
                  >
                    {loadingExcel ? "Generando..." : "Descargar Excel"}
                  </Button>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    sx={{
                      textTransform: "none",
                      borderColor: "#e0e0e0",
                      color: "#1a1a1a",
                      "&:hover": {
                        borderColor: "#1a1a1a",
                        bgcolor: "#f5f5f5",
                      },
                    }}
                  >
                    Subir archivo
                    <input type="file" hidden onChange={handleFileChange} accept=".csv,.xlsx,.xls" />
                  </Button>
                  {selectedFile && (
                    <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#666" }}>
                      Archivo seleccionado: {selectedFile.name}
                    </Typography>
                  )}
                </Box>

                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSendCampaign}
                  disabled={!selectedFile || sending}
                  sx={{
                    textTransform: "none",
                    bgcolor: "#1a1a1a",
                    "&:hover": {
                      bgcolor: "#333",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#e0e0e0",
                      color: "#999",
                    },
                  }}
                >
                  {sending ? "Enviando..." : "Enviar"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}